import React, { useState } from 'react';
import apiService from '../services/ApiService';
import CustomDropdown from './CustomDropdown';
import VehicleFieldCombobox from './VehicleFieldCombobox';
import ExteriorFinishFields, { type ExteriorFinishFormValues } from './vehicle/ExteriorFinishFields';
import { exteriorFinishToPayload, normalizeExteriorFinishType, validateExteriorFinishInput } from '../utils/exteriorFinish';
import Modal from './ui/Modal';
import Button from './ui/Button';
import { useToast } from '../contexts/ToastContext';

interface AddVehicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVehicleAdded: (vehicle: any) => void;
}

type BuildStatus = 'planned' | 'in_progress' | 'complete';

interface VehicleFormData extends ExteriorFinishFormValues {
  year: string;
  make: string;
  model: string;
  trim: string;
  chassis: string;
  engine: string;
  transmission: string;
  drivetrain: string;
  build_status: BuildStatus;
  nickname: string;
  description: string;
  is_primary: boolean;
  is_public: boolean;
  horsepower: string;
  torque: string;
  redline: string;
}

const DRIVETRAIN_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'FWD', label: 'FWD' },
  { value: 'RWD', label: 'RWD' },
  { value: 'AWD', label: 'AWD' },
  { value: '4WD', label: '4WD' },
];

const BUILD_STATUS_OPTIONS = [
  { value: 'planned', label: 'Planned' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'complete', label: 'Complete' },
];

export const AddVehicleModal: React.FC<AddVehicleModalProps> = ({
  isOpen,
  onClose,
  onVehicleAdded
}) => {
  const { showToast } = useToast();
  const [formData, setFormData] = useState<VehicleFormData>({
    year: '',
    make: '',
    model: '',
    trim: '',
    chassis: '',
    engine: '',
    transmission: '',
    drivetrain: '',
    build_status: 'in_progress',
    color: '',
    exterior_finish_type: 'paint',
    exterior_finish_color: '',
    exterior_finish_brand: '',
    exterior_finish_style: '',
    nickname: '',
    description: '',
    is_primary: false,
    is_public: true,
    horsepower: '',
    torque: '',
    redline: ''
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isAssessing, setIsAssessing] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [isCustomMake, setIsCustomMake] = useState(false);
  const [customMake, setCustomMake] = useState('');

  // Popular makes for suggestions
  const popularMakes = [
    'Acura', 'Audi', 'BMW', 'Chevrolet', 'Dodge', 'Ford', 'Honda', 'Hyundai',
    'Infiniti', 'Lexus', 'Mazda', 'Mercedes-Benz', 'Mitsubishi', 'Nissan',
    'Porsche', 'Subaru', 'Toyota', 'Volkswagen', 'Volvo'
  ];

  // Generate year options (current year down to 1980)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear - 1979 }, (_, i) => currentYear - i);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // Year validation
    if (!formData.year) {
      newErrors.year = 'Year is required';
    } else {
      const year = parseInt(formData.year);
      if (year < 1980 || year > currentYear + 1) {
        newErrors.year = 'Please enter a valid year';
      }
    }

    // Make validation
    if (!formData.make.trim()) {
      newErrors.make = 'Make is required';
    } else if (formData.make.trim().length < 2) {
      newErrors.make = 'Make must be at least 2 characters';
    }

    // Model validation
    if (!formData.model.trim()) {
      newErrors.model = 'Model is required';
    } else if (formData.model.trim().length < 1) {
      newErrors.model = 'Model is required';
    }

    // Trim validation
    if (!formData.trim.trim()) {
      newErrors.trim = 'Trim is required';
    }

    // Nickname validation (now required)
    if (!formData.nickname.trim()) {
      newErrors.nickname = 'Nickname is required';
    } else if (formData.nickname.trim().length > 100) {
      newErrors.nickname = 'Nickname must be less than 100 characters';
    }

    // Description validation (optional but if provided, check length)
    if (formData.description.trim() && formData.description.trim().length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    const finishType = normalizeExteriorFinishType(formData.exterior_finish_type);
    const finishError = validateExteriorFinishInput(finishType, formData.exterior_finish_color);
    if (finishError) {
      newErrors.exterior_finish_color = finishError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setIsAssessing(true);
    setGeneralError('');

    try {
      const vehicleData = {
        year: parseInt(formData.year),
        make: formData.make.trim(),
        model: formData.model.trim(),
        trim: formData.trim.trim(),
        color: formData.color.trim() || undefined,
        chassis: formData.chassis.trim() || undefined,
        engine: formData.engine.trim() || undefined,
        transmission: formData.transmission.trim() || undefined,
        drivetrain: formData.drivetrain.trim() || undefined,
        build_status: formData.build_status,
        ...exteriorFinishToPayload({
          exterior_finish_type: normalizeExteriorFinishType(formData.exterior_finish_type),
          exterior_finish_color: formData.exterior_finish_color,
          exterior_finish_brand: formData.exterior_finish_brand,
          exterior_finish_style: formData.exterior_finish_style,
        }),
        nickname: formData.nickname.trim() || undefined,
        description: formData.description.trim() || undefined,
        is_primary: formData.is_primary,
        is_public: formData.is_public,
        horsepower: formData.horsepower.trim() ? parseInt(formData.horsepower.trim()) : undefined,
        torque: formData.torque.trim() ? parseInt(formData.torque.trim()) : undefined,
        redline: formData.redline.trim() ? parseInt(formData.redline.trim()) : undefined
      };

      const vehicle = await apiService.createVehicle(vehicleData);
      // Ensure assessment_status is set to 'pending' for new vehicles
      if (!vehicle.assessment_status) {
        vehicle.assessment_status = 'pending';
      }
      onVehicleAdded(vehicle);
      showToast('Build added.', 'success');
      onClose();

      // Reset form
      setFormData({
        year: '',
        make: '',
        model: '',
        trim: '',
        chassis: '',
        engine: '',
        transmission: '',
        drivetrain: '',
        build_status: 'in_progress',
        color: '',
        exterior_finish_type: 'paint',
        exterior_finish_color: '',
        exterior_finish_brand: '',
        exterior_finish_style: '',
        nickname: '',
        description: '',
        is_primary: false,
        is_public: true,
        horsepower: '',
        torque: '',
        redline: ''
      });
      setErrors({});
      setIsCustomMake(false);
      setCustomMake('');

    } catch (error: any) {
      console.error('Failed to add vehicle:', error);
      setGeneralError(error.message || 'Failed to add vehicle. Please try again.');
    } finally {
      setIsLoading(false);
      setIsAssessing(false);
    }
  };

  const handleInputChange = (field: keyof VehicleFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleClose = () => {
    if (!isLoading && !isAssessing) {
      onClose();
      setErrors({});
      setGeneralError('');
      setIsCustomMake(false);
      setCustomMake('');
    }
  };

  const busy = isLoading || isAssessing;

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!busy) handleClose();
      }}
      title="Add a Build"
      description="Starts a public build page you can grow over time. Specs, photos, and a shareable QR when you’re ready."
      size="xl"
      closeOnOverlayClick={!busy}
      closeOnEscape={!busy}
      showClose={!busy}
      footer={
        <div className="flex flex-col-reverse sm:flex-row gap-3">
          <Button
            type="button"
            variant="ghost"
            onClick={handleClose}
            disabled={busy}
            className="flex-1 min-h-[44px]"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="add-vehicle-form"
            variant="primary"
            disabled={busy}
            loading={busy}
            loadingLabel="Adding your build…"
            className="flex-1 min-h-[44px]"
          >
            Add a Build
          </Button>
        </div>
      }
    >
          {isAssessing && (
            <div className="form-section mb-6">
              <div className="flex items-center space-x-3">
                <div className="spinner-sm" aria-hidden />
                <div>
                  <p className="text-gray-200 font-medium">Adding your vehicle…</p>
                  <p className="text-gray-400 text-sm">Assessing rarity and specifications.</p>
                </div>
              </div>
            </div>
          )}

          {generalError && (
            <div className="mb-4 p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-200 text-sm" role="alert">
              {generalError}
            </div>
          )}

          <form id="add-vehicle-form" onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Vehicle Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Year */}
              <div>
                <label htmlFor="year" className="input-label">
                  Year *
                </label>
                <CustomDropdown
                  value={formData.year}
                  onChange={(value) => handleInputChange('year', value)}
                  options={yearOptions.map(year => ({
                    value: year.toString(),
                    label: year.toString()
                  }))}
                  placeholder="Select Year"
                  disabled={isLoading || isAssessing}
                  error={!!errors.year}
                />
                {errors.year && (
                  <p className="mt-1 text-sm text-red-400">{errors.year}</p>
                )}
              </div>

              {/* Make */}
              <div>
                <label htmlFor="make" className="input-label">
                  Make *
                </label>
                {!isCustomMake ? (
                  <div className="space-y-2">
                    <CustomDropdown
                      value={formData.make}
                      onChange={(value) => {
                        if (value === 'other') {
                          setIsCustomMake(true);
                          handleInputChange('make', '');
                        } else {
                          handleInputChange('make', value);
                        }
                      }}
                      options={[
                        ...popularMakes.map(make => ({
                          value: make,
                          label: make
                        })),
                        { value: 'other', label: 'Other (Custom)' }
                      ]}
                      placeholder="Select Make"
                      disabled={isLoading || isAssessing}
                      error={!!errors.make}
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <input
                      type="text"
                      id="make-custom"
                      value={customMake}
                      onChange={(e) => {
                        setCustomMake(e.target.value);
                        handleInputChange('make', e.target.value);
                      }}
                      className={`input ${errors.make ? 'border-red-500' : ''}`}
                      placeholder="Enter custom make"
                      disabled={isLoading || isAssessing}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setIsCustomMake(false);
                        setCustomMake('');
                        handleInputChange('make', '');
                      }}
                      className="text-sm text-orange-400 hover:text-orange-300 transition-colors"
                    >
                      ← Back to popular makes
                    </button>
                  </div>
                )}
                {errors.make && (
                  <p className="mt-1 text-sm text-red-400">{errors.make}</p>
                )}
              </div>

              {/* Model */}
              <div>
                <label htmlFor="model" className="input-label">
                  Model *
                </label>
                <input
                  type="text"
                  id="model"
                  value={formData.model}
                  onChange={(e) => handleInputChange('model', e.target.value)}
                  className={`input ${errors.model ? 'border-red-500' : ''}`}
                  placeholder="e.g., Lancer Evolution"
                  disabled={isLoading || isAssessing}
                />
                {errors.model && (
                  <p className="mt-1 text-sm text-red-400">{errors.model}</p>
                )}
              </div>

              {/* Trim */}
              <div>
                <label htmlFor="trim" className="input-label">
                  Trim *
                </label>
                <input
                  type="text"
                  id="trim"
                  value={formData.trim}
                  onChange={(e) => handleInputChange('trim', e.target.value)}
                  className={`input ${errors.trim ? 'border-red-500' : ''}`}
                  placeholder="e.g., 9 GT Wagon"
                  disabled={isLoading || isAssessing}
                />
                {errors.trim && (
                  <p className="mt-1 text-sm text-red-400">{errors.trim}</p>
                )}
              </div>
            </div>

            <ExteriorFinishFields
              values={formData}
              onChange={(field, value) => handleInputChange(field, value)}
              disabled={isLoading || isAssessing}
              errors={errors}
            />

            {/* Build specs */}
            <div>
              <h3 className="text-sm font-semibold text-gray-200 mb-3">Build specs</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="chassis" className="input-label">
                    Chassis
                  </label>
                  <VehicleFieldCombobox
                    field="chassis"
                    id="chassis"
                    value={formData.chassis}
                    onChange={(v) => handleInputChange('chassis', v)}
                    placeholder="e.g., WC34"
                    disabled={isLoading || isAssessing}
                  />
                </div>

                <div>
                  <label htmlFor="engine" className="input-label">
                    Engine
                  </label>
                  <VehicleFieldCombobox
                    field="engine"
                    id="engine"
                    value={formData.engine}
                    onChange={(v) => handleInputChange('engine', v)}
                    placeholder="e.g., RB26DETT"
                    disabled={isLoading || isAssessing}
                  />
                </div>

                <div>
                  <label htmlFor="transmission" className="input-label">
                    Transmission
                  </label>
                  <VehicleFieldCombobox
                    field="transmission"
                    id="transmission"
                    value={formData.transmission}
                    onChange={(v) => handleInputChange('transmission', v)}
                    placeholder="e.g., 5-speed manual"
                    disabled={isLoading || isAssessing}
                  />
                </div>

                <div>
                  <label htmlFor="drivetrain" className="input-label">
                    Drivetrain
                  </label>
                  <CustomDropdown
                    value={formData.drivetrain}
                    onChange={(value) => handleInputChange('drivetrain', value)}
                    options={DRIVETRAIN_OPTIONS}
                    placeholder="Select Drivetrain"
                    disabled={isLoading || isAssessing}
                  />
                </div>

                <div>
                  <label htmlFor="build_status" className="input-label">
                    Build status
                  </label>
                  <CustomDropdown
                    value={formData.build_status}
                    onChange={(value) => handleInputChange('build_status', value)}
                    options={BUILD_STATUS_OPTIONS}
                    placeholder="Select Build Status"
                    disabled={isLoading || isAssessing}
                  />
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="nickname" className="input-label">
                Nickname *
              </label>
              <input
                type="text"
                id="nickname"
                value={formData.nickname}
                onChange={(e) => handleInputChange('nickname', e.target.value)}
className={`input ${errors.nickname ? 'border-red-500' : ''}`}
                placeholder="e.g., The Beast, Daily Driver"
                disabled={isLoading || isAssessing}
              />
              {errors.nickname && (
                <p className="mt-1 text-sm text-red-400">{errors.nickname}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="input-label">
                Description
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
className={`input resize-none ${errors.description ? 'border-red-500' : ''}`}
                placeholder="Brief description of your vehicle and build goals..."
                disabled={isLoading || isAssessing}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-400">{errors.description}</p>
              )}
            </div>

            {/* Options */}
            <div className="space-y-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_primary"
                  checked={formData.is_primary}
                  onChange={(e) => handleInputChange('is_primary', e.target.checked)}
                  className="checkbox-control"
                  disabled={isLoading || isAssessing}
                />
                <label htmlFor="is_primary" className="ml-2 text-sm text-gray-300">
                  Set as primary vehicle (featured on your profile)
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_public"
                  checked={formData.is_public}
                  onChange={(e) => handleInputChange('is_public', e.target.checked)}
                  className="checkbox-control"
                  disabled={isLoading || isAssessing}
                />
                <label htmlFor="is_public" className="ml-2 text-sm text-gray-300">
                  Make vehicle public (visible to other users)
                </label>
              </div>
            </div>
          </form>
    </Modal>
  );
}; 